import { DOCUMENT } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    Inject,
    Renderer2,
    OnDestroy,
    NgZone,
    ChangeDetectorRef
} from '@angular/core';
import {
    AppStartupService,
    HeaderElement,
    Language,
    LogService,
    NetworkService,
    OnboardingService
} from '@ridango/core';
import { HeaderMenuService } from '../../../services/header-menu.service';
import { HeaderComponent } from '../header.component';
import Env from '@ridango/core/env';
import { TranslateService } from '@ngx-translate/core';

import {
    ApplicationFacade,
    CustomerFacade,
    CustomerService,
    LoginFacade,
    NotificationFacade,
    SessionService
} from '@ridango/components';

import { Router } from '@angular/router';
import { HelperService } from '../../../services/helper.service';
import { LocaleService } from '../../../../services/locale.service';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { Subject } from 'rxjs';
import { ConnectedPosition } from '@angular/cdk/overlay';

@Component({
    selector: 'gl-header-stw',
    templateUrl: './header-stw.component.html',
    styleUrls: ['./header-stw.component.scss']
})
export class HeaderStwComponent extends HeaderComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private closeTimeout?: ReturnType<typeof setTimeout>;
    private openTimeout?: ReturnType<typeof setTimeout>;

    readonly dropdownMenuConfig: Record<number, { width: number }> = {
        1: { width: 660 },
        2: { width: 600 },
        3: { width: 300 },
        4: { width: 400 }
    };

    @Input() mobileMenuOpen = false;
    @Input() languages: Language[];
    @Input() selected: Language;
    @Input() isContrast: boolean;
    @Output() languageChange: EventEmitter<Language> = new EventEmitter();
    @Output() mobileMenuToggle: EventEmitter<void> = new EventEmitter();
    @Output() mobileMenuClose: EventEmitter<void> = new EventEmitter();
    @Output() themeToggle: EventEmitter<void> = new EventEmitter();

    isOpened = false;
    logoFile: string;
    logoFileContrast: string;
    hideShoppingIcon: boolean;
    menuItems: HeaderElement[];
    visibleMenu: number | null = null;
    dropdownMenuItems$ = this.headerMenuService.dropdownMenuItems$;
    useDropdownMenuSystem$ = this.headerMenuService.useDropdownMenuSystem$;
    dropdownMenuData$ = this.headerMenuService.getDropdownMenuData();
    dropdownMenuPositions: ConnectedPosition[] = [
        {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: -8
        }
    ];

    constructor(
        appConfig: AppStartupService,
        applicationFacade: ApplicationFacade,
        eRef: ElementRef,
        ls: LogService,
        @Inject(DOCUMENT) document: any,
        router: Router,
        ridangoAuth: SessionService,
        notification: NotificationFacade,
        renderer: Renderer2,
        helper: HelperService,
        loginFacade: LoginFacade,
        translate: TranslateService,
        @Inject('ENV') env: Env,
        onboardingService: OnboardingService,
        customerService: CustomerService,
        customerFacade: CustomerFacade,
        networkService: NetworkService,
        localeService: LocaleService,
        public headerMenuService: HeaderMenuService,
        analyticsService: AnalyticsService,
        private ngZone: NgZone,
        private cdr: ChangeDetectorRef
    ) {
        super(
            appConfig,
            applicationFacade,
            eRef,
            ls,
            document,
            router,
            localeService,
            ridangoAuth,
            notification,
            renderer,
            helper,
            loginFacade,
            translate,
            env,
            onboardingService,
            customerService,
            customerFacade,
            networkService,
            headerMenuService,
            analyticsService
        );
        super.ngOnInit();
    }

    ngOnInit(): void {
        this.logoFile = this.env.HEADER?.LOGO_FILE ?? 'logo.svg';
        this.logoFileContrast = this.env.HEADER?.LOGO_FILE_CONTRAST ?? 'logo-wcag.svg';
        this.hideShoppingIcon = this.env.HEADER?.HIDE_SHOPPING_CART ?? true;
        this.initializeMenu();
    }

    initializeMenu(): void {
        this.menuItems = this.headerMenuService.getMenuItems();
    }

    isMenuItemAllowed(headerElement: HeaderElement): boolean {
        return this.headerMenuService.isCheckBasicConditions(headerElement);
    }

    changeLanguage(lang) {
        this.languageChange.emit(lang);
    }

    toggleMobileMenu() {
        this.mobileMenuToggle.emit();
    }

    closeMobileMenu() {
        this.mobileMenuClose.emit();
    }

    toggleContrast() {
        this.themeToggle.emit();
    }

    isDropdownMenuItemAllowed(menuItem: any): boolean {
        return this.headerMenuService.isDropdownMenuItemAllowed(menuItem);
    }

    toggleMenu(menuId: number): void {
        this.cancelClose();
        this.cancelOpen();

        if (this.visibleMenu !== null) {
            this.visibleMenu = menuId;
            this.cdr.markForCheck();
        } else {
            this.openTimeout = setTimeout(() => {
                this.visibleMenu = menuId;
                this.cdr.markForCheck();
            }, 0);
        }
    }

    cancelOpen(): void {
        if (this.openTimeout) {
            clearTimeout(this.openTimeout);
            this.openTimeout = undefined;
        }
    }

    scheduleClose(): void {
        this.cancelOpen();
        this.ngZone.runOutsideAngular(() => {
            this.closeTimeout = setTimeout(() => {
                this.ngZone.run(() => {
                    this.visibleMenu = null;
                    this.cdr.markForCheck();
                });
            }, 120);
        });
    }

    cancelClose(): void {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
            this.closeTimeout = undefined;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
